import React, { createContext, useContext, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUrlState } from '../hooks/useUrlState';

const TimeRangeContext = createContext();

export const useTimeRange = () => {
  const context = useContext(TimeRangeContext);
  if (!context) {
    throw new Error('useTimeRange must be used within a TimeRangeProvider');
  }
  return context;
};

export const TimeRangeProvider = ({ children }) => {
  const [selectedRange, setSelectedRange] = useUrlState('range', '24hours');
  const location = useLocation();
  const navigate = useNavigate();

  // Wrap setSelectedRange to capture current location state
  const wrappedSetSelectedRange = useCallback((newRange) => {
    setSelectedRange(newRange);
  }, [setSelectedRange]);

  // Ensure range is always in URL (backup enforcement)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (!params.get('range') && selectedRange) {
      params.set('range', selectedRange);
      navigate(`${location.pathname}?${params.toString()}`, {
        replace: true,
        state: location.state,
      });
    }
  }, [location.pathname, location.search, selectedRange, navigate]);

  const getDisplayLabel = (preset) => {
    switch (preset) {
      case '1h': return 'Last 1h';
      case '24hours': return 'Last 24h';
      case '7days': return 'Last 7d';
      case '30days': return 'Last 30d';
      case 'all-time': return 'All time';
      default: return 'Last 24h';
    }
  };

  const value = useMemo(() => ({
    selectedRange,
    setSelectedRange: wrappedSetSelectedRange,
    preset: selectedRange, // For compatibility with mock app
    setPreset: wrappedSetSelectedRange, // For compatibility with mock app
    displayLabel: getDisplayLabel(selectedRange)
  }), [selectedRange, wrappedSetSelectedRange]);

  return (
    <TimeRangeContext.Provider value={value}>
      {children}
    </TimeRangeContext.Provider>
  );
};
